import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageOpening, Section, SectionTitle, MarginNote } from "../../public/Journal";
import { ScrollTrigger } from "../../public/gsap";

// Shared parts for the docs pages, set as appendices to "The File".
// Everything here prints as plain black on white: scroll-triggered motion is
// completed before printing so no heading or rule is left hidden.

// Finish every scroll-driven animation before the browser paginates.
export function usePrintReady() {
    useEffect(() => {
        const finish = () => ScrollTrigger.getAll().forEach((st) => st.animation?.progress(1));
        window.addEventListener("beforeprint", finish);
        return () => window.removeEventListener("beforeprint", finish);
    }, []);
}

const control =
    "link-underline font-plex-mono text-[12px] uppercase tracking-[0.18em] text-ink/60 hover:text-ink dark:text-paper/60 dark:hover:text-paper transition-colors";

// Back link and print control: small mono text, hidden in print.
export function DocControls() {
    const navigate = useNavigate();
    return (
        <div className="flex items-center justify-between pt-8 print:hidden">
            <button type="button" onClick={() => navigate("/resources")} className={control}>
                ← Back to Resources
            </button>
            <button type="button" onClick={() => window.print()} className={control}>
                Print / Save PDF
            </button>
        </div>
    );
}

export function DocOpening({ appendix, label, title, intro }) {
    return (
        <div className="print:text-black">
            <DocControls />
            <PageOpening section={`Appendix ${appendix}`} label={label} title={title} intro={intro} />
        </div>
    );
}

export function DocSection({ number, label, title, aside, children }) {
    return (
        <Section number={number} label={label} aside={aside} className="print:py-8">
            <SectionTitle className="print:text-black">{title}</SectionTitle>
            <div className="mt-8 font-plex text-[16.5px] [&>h3:first-child]:mt-0 leading-[1.75] text-ink/75 dark:text-paper/75 print:text-black">
                {children}
            </div>
        </Section>
    );
}

export function SubHead({ children }) {
    return (
        <h3 className="mb-4 mt-12 font-news text-[24px] font-normal leading-tight tracking-[-0.01em] text-ink dark:text-paper print:text-black">
            {children}
        </h3>
    );
}

export function P({ children, className = "" }) {
    return <p className={`mb-4 max-w-2xl ${className}`}>{children}</p>;
}

// Emphasised lead-in term, e.g. "Email:".
export function T({ children }) {
    return <span className="font-medium text-ink dark:text-paper print:text-black">{children}</span>;
}

// Numbered steps: mono step numbers on hairline rules.
export function Steps({ items }) {
    return (
        <ol className="max-w-3xl border-b border-ink/15 dark:border-paper/15">
            {items.map((item, i) => (
                <li key={i} className="grid grid-cols-[52px_1fr] border-t border-ink/15 py-3.5 dark:border-paper/15">
                    <span className="font-plex-mono text-[12px] leading-[28px] text-stamp print:text-black">
                        {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{item}</span>
                </li>
            ))}
        </ol>
    );
}

// Unordered list with a short ruled dash as the marker.
export function List({ items, className = "" }) {
    return (
        <ul className={`max-w-3xl space-y-2.5 ${className}`}>
            {items.map((item, i) => (
                <li key={i} className="relative pl-6">
                    <span aria-hidden="true" className="absolute left-0 top-[0.85em] h-px w-3 bg-ink/40 dark:bg-paper/40 print:bg-black" />
                    {item}
                </li>
            ))}
        </ul>
    );
}

// Ruled callout with a red mono label, used where a margin note won't do.
export function Note({ label = "Note", children }) {
    return (
        <div className="my-8 max-w-2xl border-l border-stamp py-1 pl-5 print:border-black">
            <p className="mb-1.5 font-plex-mono text-[11px] uppercase tracking-[0.2em] text-stamp print:text-black">{label}</p>
            <div className="text-[15.5px] text-ink/80 dark:text-paper/80 print:text-black">{children}</div>
        </div>
    );
}

export { MarginNote };

export function Code({ children }) {
    return (
        <pre className="my-6 max-w-3xl overflow-x-auto border border-ink/15 bg-paper-50 p-5 font-plex-mono text-[13px] leading-[1.7] text-ink/85 dark:border-paper/15 dark:bg-ink-900 dark:text-paper/85 print:border-black print:bg-white print:text-black print:whitespace-pre-wrap">
            {children}
        </pre>
    );
}

export function InlineCode({ children }) {
    return (
        <code className="border-b border-ink/25 font-plex-mono text-[0.88em] text-ink dark:border-paper/25 dark:text-paper print:text-black">
            {children}
        </code>
    );
}

export function MailLink({ href = "mailto:jpro@zeffert.com", children = "jpro@zeffert.com" }) {
    return (
        <a href={href} className="link-underline text-stamp print:text-black">
            {children}
        </a>
    );
}

// Ruled ledger table with a mono header row.
export function DocTable({ head, rows }) {
    return (
        <div className="my-6 max-w-4xl overflow-x-auto">
            <table className="w-full border-collapse text-left text-[15px]">
                <thead>
                    <tr className="border-b border-ink/60 dark:border-paper/50 print:border-black">
                        {head.map((h) => (
                            <th key={h} className="py-3 pr-6 font-plex-mono text-[11px] font-normal uppercase tracking-[0.16em] text-ink/55 dark:text-paper/55 print:text-black">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="border-b border-ink/15 dark:border-paper/15 print:border-black/40">
                            {row.map((cell, j) => (
                                <td key={j} className={`py-3 pr-6 align-top ${j === 0 ? "font-medium text-ink dark:text-paper print:text-black" : ""}`}>
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function Colophon({ children }) {
    return (
        <div className="border-t border-ink/15 py-10 text-center font-plex-mono text-[11.5px] uppercase tracking-[0.16em] text-ink/45 dark:border-paper/15 dark:text-paper/45 print:text-black">
            {children}
        </div>
    );
}
